import { EnvironmentStep } from '@/components/environment-step';
import { QuestionField } from '@/components/question-field';
import { Button, buttonVariants } from '@/components/ui/button';
import { API_URL, ApiError, api } from '@/lib/api';
import { seedAnswers } from '@/lib/creation-defaults';
import { computeForced } from '@/lib/requires';
import { slugify } from '@/lib/slugify';
import { visibleAnswers } from '@/lib/visible-answers';
import { evaluateWhen } from '@/lib/when';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/')({
  component: WizardPage,
});

type Answers = Record<string, unknown>;

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return `${err.message} (${err.status})`;
  return err instanceof Error ? err.message : 'Unknown error';
}

/** Apply `_requires`: returns the forced map + answers with forced toggles set true. */
function applyRequires(
  answers: Answers,
  requires: Record<string, string[]>,
): { forced: Record<string, string>; effective: Answers } {
  const forced = computeForced(answers, requires);
  const effective: Answers = { ...answers };
  for (const key of Object.keys(forced)) effective[key] = true;
  return { forced, effective };
}

/** The slug to show/submit: the user's value if set, else derived from the name. */
function effectiveSlug(answers: Answers): string {
  return answers.project_slug
    ? String(answers.project_slug)
    : slugify(String(answers.project_name ?? ''));
}

function WizardPage() {
  const [envReady, setEnvReady] = useState(false);
  const [tokenInput, setTokenInput] = useState('');
  const [token, setToken] = useState('');
  const [templatePath, setTemplatePath] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const [steps, setSteps] = useState<string[]>([]);

  const queryClient = useQueryClient();
  const authQuery = useQuery({ queryKey: ['auth', 'me'], queryFn: api.getAuthStatus });
  const user = authQuery.data?.user ?? null;
  const oauthEnabled = authQuery.data?.oauthEnabled ?? false;
  const connected = user !== null || token.length > 0;

  const logout = useMutation({
    mutationFn: api.logout,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['auth', 'me'] }),
  });

  const templatesQuery = useQuery({
    queryKey: ['templates', user?.username ?? token],
    queryFn: () => api.listTemplates(token),
    enabled: connected && templatePath === null,
  });

  const questionsQuery = useQuery({
    queryKey: ['questions', user?.username ?? token, templatePath],
    queryFn: () => api.getQuestions(token, templatePath ?? ''),
    enabled: connected && templatePath !== null,
  });

  const generate = useMutation({
    mutationFn: () => {
      const requires = questionsQuery.data?.requires ?? {};
      const { effective } = applyRequires(answers, requires);
      // Only what the form shows: copier renders with an answer to a hidden
      // question yet leaves it out of the answers file.
      const submitted = visibleAnswers(questionsQuery.data?.questions ?? [], effective);
      submitted.project_slug = effectiveSlug(answers);
      return api.generate(
        token,
        { templatePath: templatePath ?? '', answers: submitted },
        (label) => setSteps((prev) => [...prev, label]),
      );
    },
    onMutate: () => setSteps([]),
  });

  // Derive form state from fetched questions (adjust-state-during-render, not useEffect).
  if (questionsQuery.data && seededFor !== templatePath) {
    setAnswers(seedAnswers(questionsQuery.data.questions));
    setSeededFor(templatePath);
  }

  const setAnswer = (name: string, value: unknown): void => {
    setAnswers((prev) => ({ ...prev, [name]: value }));
  };

  const reset = (): void => {
    setTemplatePath(null);
    setAnswers({});
    setSeededFor(null);
    setSteps([]);
    generate.reset();
  };

  // ── Environment pre-step ──────────────────────────────────────────────────────
  if (!envReady) {
    return <EnvironmentStep onContinue={() => setEnvReady(true)} />;
  }

  // ── Done ────────────────────────────────────────────────────────────────────
  if (generate.data) {
    const r = generate.data;
    return (
      <section className="space-y-4">
        <h1 className="font-bold text-2xl text-bleu-nuit">✅ Project created</h1>
        <p>
          <span className="font-medium">{r.projectName}</span> generated (smoke: {r.smoke}).
        </p>
        <p className="text-sm">
          <a className="text-bleu-nuit underline" href={r.webUrl} target="_blank" rel="noreferrer">
            {r.webUrl}
          </a>
        </p>
        {r.warnings.length > 0 ? (
          <div
            role="alert"
            className="space-y-1 rounded border border-jaune-deep/30 bg-jaune-tint p-3 text-sm"
          >
            <p className="font-medium">Created, but check this:</p>
            <ul className="list-disc space-y-1 pl-5">
              {r.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <pre className="rounded bg-gray-100 p-3 text-sm">git clone {r.sshUrl}</pre>
        <Button type="button" onClick={reset}>
          Create another
        </Button>
      </section>
    );
  }

  // -- Connect (OAuth or manual token) --------------------------------------
  if (!connected) {
    return (
      <section className="space-y-4">
        <h1 className="font-bold text-2xl text-bleu-nuit">New project</h1>
        {authQuery.isPending ? <p>Checking session...</p> : null}
        {authQuery.isError ? (
          <p className="text-danger text-sm">
            Could not check authentication status - try reloading.
          </p>
        ) : null}
        {!authQuery.isPending && oauthEnabled ? (
          <>
            <p className="text-sm opacity-70">
              Sign in with your GitLab account - projects are created in your name.
            </p>
            <a href={`${API_URL}/auth/login`} className={buttonVariants()}>
              Sign in with GitLab
            </a>
          </>
        ) : null}
        {!authQuery.isPending && !oauthEnabled ? (
          <>
            <p className="text-sm opacity-70">
              Paste a GitLab token with the <code>api</code> scope. It stays in memory only.
            </p>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setToken(tokenInput.trim());
              }}
            >
              <input
                type="password"
                className="flex-1 rounded border border-gray-300 px-3 py-2"
                placeholder="glpat-..."
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
              />
              <Button type="submit" disabled={!tokenInput.trim()}>
                Continue
              </Button>
            </form>
          </>
        ) : null}
      </section>
    );
  }

  // ── Pick a template ────────────────────────────────────────────────────────────
  if (templatePath === null) {
    return (
      <section className="space-y-4">
        <h1 className="font-bold text-2xl text-bleu-nuit">Choose a template</h1>
        {templatesQuery.isPending ? <p>Loading templates…</p> : null}
        {templatesQuery.isError ? (
          <p className="text-danger">{errorMessage(templatesQuery.error)}</p>
        ) : null}
        <ul className="space-y-2">
          {templatesQuery.data?.map((t) => (
            <li key={t.path}>
              <button
                type="button"
                className="w-full rounded border border-gray-300 p-3 text-left hover:bg-gray-50"
                onClick={() => setTemplatePath(t.path)}
              >
                <span className="font-medium">{t.name}</span>
                {t.description ? (
                  <span className="block text-sm opacity-70">{t.description}</span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
        {user ? (
          <div className="flex items-center gap-2 text-sm opacity-70">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="h-6 w-6 rounded-full" />
            ) : null}
            <span>
              Signed in as <span className="font-medium">{user.name}</span>
            </span>
            <Button
              type="button"
              onClick={() => logout.mutate()}
              disabled={logout.isPending || generate.isPending}
            >
              Sign out
            </Button>
          </div>
        ) : (
          <Button type="button" onClick={() => setToken('')}>
            Use a different token
          </Button>
        )}
      </section>
    );
  }

  // ── Configure (dynamic form) ────────────────────────────────────────────────────
  const requires = questionsQuery.data?.requires ?? {};
  const { forced, effective } = applyRequires(answers, requires);
  const slugValue = effectiveSlug(answers);
  const questions = questionsQuery.data?.questions ?? [];
  const shown = visibleAnswers(questions, effective);
  const visible = questions.filter((q) => evaluateWhen(q.when, shown));

  return (
    <section className="space-y-4">
      <h1 className="font-bold text-2xl text-bleu-nuit">Configure {templatePath}</h1>
      {questionsQuery.isPending ? <p>Loading questions…</p> : null}
      {questionsQuery.isError ? (
        <p className="text-danger">{errorMessage(questionsQuery.error)}</p>
      ) : null}
      {questionsQuery.data && !questionsQuery.data.copier ? (
        <p className="rounded border border-jaune-deep/30 bg-jaune-tint p-3 text-sm">
          This template has no <code>copier.yml</code>. It will be cloned as-is — no questions, no
          smoke tests — exactly like the original <code>new-project</code> script.
        </p>
      ) : null}

      <form
        className="space-y-1"
        onSubmit={(e) => {
          e.preventDefault();
          generate.mutate();
        }}
      >
        {visible.map((q) => {
          const forcedBy = forced[q.name];
          const isSlug = q.name === 'project_slug';
          const note = isSlug
            ? 'used as the GitLab project path / URL'
            : forcedBy
              ? `Required by ${forcedBy}`
              : undefined;
          return (
            <QuestionField
              key={q.name}
              question={q}
              value={isSlug ? slugValue : effective[q.name]}
              disabled={Boolean(forcedBy)}
              note={note}
              onChange={setAnswer}
            />
          );
        })}

        {generate.isError ? <p className="text-danger">{errorMessage(generate.error)}</p> : null}

        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={generate.isPending || !answers.project_name}>
            {generate.isPending ? 'Creating…' : 'Create project'}
          </Button>
          <Button type="button" onClick={reset}>
            Back
          </Button>
        </div>

        {generate.isPending ? (
          <div className="pt-2 text-sm">
            {steps.length === 0 ? (
              <p>
                <span className="inline-block animate-pulse">⏳</span> Starting…
              </p>
            ) : (
              <ul className="space-y-1">
                {steps.map((label, i) => (
                  <li key={label}>
                    {i === steps.length - 1 ? (
                      <span className="inline-block animate-pulse">⏳</span>
                    ) : (
                      <span className="text-success">✓</span>
                    )}{' '}
                    {label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </form>
    </section>
  );
}
